/**
 * ReviewConfigStrip
 *
 * A compact toolbar strip for matter-level settings during Initial Review.
 * Contains: Case Manager selector, auto-assign toggle, Contact Client toggle, Billing Code.
 * Positioned between the modal header and the three-column layout.
 *
 * Uses a composable ConfigItem pattern for easy addition of new config items.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CaseManagerSelector } from '@/components/case-manager';
import { getBillingOptionsFlatForFirm } from '@/components/matter-form/billingConfig';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetFirmCaseManagersQuery,
  useUpdateCaseManagerConfigMutation,
} from '@/services/api/caseManagerService';
import { useGetFirmBillingCodeStatsQuery } from '@/services/api/firmsService';
import {
  usePatchMatterClientMutation,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import {
  type DetailedMatter,
  SimplifiedBillingCode,
} from '@/services/types/client-intake-types';
import {
  PREFERRED_LANGUAGE_OPTIONS,
  PreferredLanguage,
  type PreferredLanguageValue,
} from '@/services/types/matter-client-types';
import { toastWithProgress } from '@/utils/toast';

import { Checkbox, ConfigDivider, ConfigItem } from './config-strip-utils';

interface ReviewConfigStripProps {
  matter: DetailedMatter | undefined;
  matterId: string;
  /** Whether client can be contacted directly */
  canContactClient: boolean;
  /** Callback when client contact toggle changes */
  onContactClientChange: (checked: boolean) => void;
  /** Whether the toggle is disabled (loading state) */
  isContactToggleDisabled?: boolean;
  /** Whether to highlight the billing code selector as a nudge */
  highlightBillingCode?: boolean;
}

export const ReviewConfigStrip = ({
  matter,
  matterId,
  canContactClient,
  onContactClientChange,
  isContactToggleDisabled = false,
  highlightBillingCode = false,
}: ReviewConfigStripProps) => {
  // Local state for auto task assignment
  // Initialize from matter data if available, otherwise default to false (safe default)
  const [autoTaskAssignment, setAutoTaskAssignment] = useState(() => {
    const matterAutoAssign = (
      matter?.case_manager as { auto_task_assignment?: boolean } | null
    )?.auto_task_assignment;
    return matterAutoAssign ?? false;
  });

  // API mutations
  const [updateConfig, { isLoading: isUpdatingConfig }] =
    useUpdateCaseManagerConfigMutation();
  const [patchMatter, { isLoading: isPatchingMatter }] =
    usePatchMatterMutation();
  const [patchMatterClient, { isLoading: isPatchingClient }] =
    usePatchMatterClientMutation();

  // Fetch case managers and billing stats for the firm
  const firmId = matter?.firm?.id;
  const { data: caseManagers = [] } = useGetFirmCaseManagersQuery(
    { firmId: firmId || '' },
    { skip: !firmId },
  );
  const { data: billingStats } = useGetFirmBillingCodeStatsQuery(
    firmId ?? '',
    { skip: !firmId },
  );
  const billingOptions = useMemo(
    () => getBillingOptionsFlatForFirm(billingStats),
    [billingStats],
  );

  const hasCaseManagers = caseManagers.length > 0;
  const hasCaseManagerAssigned = Boolean(matter?.case_manager?.id);

  // Sync state when matter data changes
  // If auto_task_assignment is not available, default from CM's case_auto_assign
  useEffect(() => {
    const matterAutoAssign = (
      matter?.case_manager as { auto_task_assignment?: boolean } | null
    )?.auto_task_assignment;
    if (matterAutoAssign !== undefined) {
      setAutoTaskAssignment(matterAutoAssign);
    } else if (matter?.case_manager?.id) {
      // Fallback: default to the CM's case_auto_assign setting (safe default: false)
      const cm = caseManagers.find((c) => c.id === matter.case_manager?.id);
      setAutoTaskAssignment(cm?.case_auto_assign ?? false);
    }
  }, [matter?.case_manager, caseManagers]);

  // Handle toggle change - persist to backend only if CM is already assigned
  const handleAutoAssignToggle = async () => {
    const newValue = !autoTaskAssignment;
    setAutoTaskAssignment(newValue); // Optimistic update (or just local state if no CM yet)

    // Only call backend if a CM is already assigned (otherwise it's just local state for the next assignment)
    if (hasCaseManagerAssigned) {
      if (newValue) {
        // Toggling ON — may reassign tasks, show progress toast
        try {
          await toastWithProgress(
            () =>
              updateConfig({
                matterId,
                data: { auto_task_assignment: newValue },
              }).unwrap(),
            {
              loading: 'Enabling auto-assign, updating tasks...',
              success: (result) => {
                const count = result.tasks_reassigned;
                return count > 0
                  ? `Auto-assign enabled, ${count} task${count === 1 ? '' : 's'} reassigned`
                  : 'Auto-assign enabled';
              },
              error: 'Failed to update auto-assign setting',
            },
          );
        } catch {
          setAutoTaskAssignment(!newValue);
        }
      } else {
        // Toggling OFF — no task reassignment, simple update
        try {
          await updateConfig({
            matterId,
            data: { auto_task_assignment: newValue },
          }).unwrap();
        } catch {
          setAutoTaskAssignment(!newValue);
          toast.error('Failed to update auto-assign setting');
        }
      }
    }
  };

  // Handle billing code change
  const handleBillingCodeChange = async (value: string) => {
    // Cast is safe: SelectItem values come from BILLING_OPTIONS which uses SimplifiedBillingCode enum
    const billingCode =
      value === 'none' ? null : (value as SimplifiedBillingCode);
    try {
      await patchMatter({
        matter_id: matterId,
        patch: { simplified_billing_code: billingCode },
      }).unwrap();
      toast.success(
        billingCode ? 'Billing code updated' : 'Billing code cleared',
      );
    } catch {
      toast.error('Failed to update billing code');
    }
  };

  // Handle preferred language change
  const primaryClient = matter?.clients?.[0];
  const currentLanguage = primaryClient?.preferred_language ?? PreferredLanguage.ENGLISH;

  const handleLanguageChange = async (value: string) => {
    if (!primaryClient?.id) return;
    const language = value as PreferredLanguageValue;
    try {
      await patchMatterClient({
        matter_id: matterId,
        matter_client_id: primaryClient.id,
        patch: { preferred_language: language },
      }).unwrap();
      toast.success(`Language set to ${language}`);
    } catch {
      toast.error('Failed to update preferred language');
    }
  };

  return (
    <div className="flex-shrink-0 shadow-inner bg-zinc-100 dark:bg-zinc-900 px-3 py-1.5">
      <div className="flex items-start gap-6">
        {/* Case Manager Config */}
        {hasCaseManagers && firmId && (
          <>
            <ConfigItem label="Case Manager">
              <CaseManagerSelector
                matterId={matterId}
                currentCaseManagerId={matter?.case_manager?.id ?? null}
                currentCaseManager={matter?.case_manager ?? null}
                caseManagers={caseManagers}
                compact
                autoTaskAssignmentOverride={autoTaskAssignment}
              />
              <Checkbox
                id="auto-assign-tasks"
                checked={autoTaskAssignment}
                onChange={handleAutoAssignToggle}
                disabled={hasCaseManagerAssigned && isUpdatingConfig}
                label="Auto-assign (toggle before selecting)"
              />
            </ConfigItem>
            <ConfigDivider />
          </>
        )}

        {/* Client Contact Allowed */}
        <ConfigItem label="Client Contact Allowed">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              role="switch"
              aria-checked={canContactClient}
              disabled={isContactToggleDisabled}
              onClick={() => onContactClientChange(!canContactClient)}
              className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                canContactClient
                  ? 'bg-indigo-600'
                  : 'bg-zinc-300 dark:bg-zinc-700'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition-transform ${
                  canContactClient ? 'translate-x-3' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="text-xs text-zinc-600 dark:text-zinc-400">
              {canContactClient ? 'Yes' : 'No'}
            </span>
          </div>
        </ConfigItem>

        <ConfigDivider />

        {/* Billing Code Selector */}
        <ConfigItem label="Billing Code">
          <Select
            value={matter?.simplified_billing_code ?? 'none'}
            onValueChange={handleBillingCodeChange}
            disabled={isPatchingMatter}
          >
            <SelectTrigger className={`h-6 min-w-[140px] px-2 text-xs bg-white dark:bg-zinc-800 transition-all duration-300 ${highlightBillingCode ? 'border-amber-500 ring-2 ring-amber-400/50 shadow-[0_0_8px_rgba(245,158,11,0.3)]' : 'border-zinc-200 dark:border-zinc-700'}`}>
              <SelectValue placeholder="Select...">
                {matter?.simplified_billing_code ? (
                  <span className="truncate font-medium text-zinc-900 dark:text-white">
                    {billingOptions.find(
                      (o) => o.code === matter.simplified_billing_code,
                    )?.label ?? 'Select...'}
                  </span>
                ) : (
                  <span className="text-zinc-500">Select...</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[240px]">
              <SelectItem value="none" className="text-xs text-zinc-500">
                Not set
              </SelectItem>
              {billingOptions.map((option) => (
                <SelectItem
                  key={option.code}
                  value={option.code}
                  className="text-xs"
                >
                  <div className="flex flex-col">
                    <span>
                      {option.label}
                      {option.note && (
                        <span className="text-zinc-500 dark:text-zinc-400 ml-1">
                          ({option.note})
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
                      {option.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ConfigItem>

        <ConfigDivider />

        {/* Client Preferred Language */}
        <ConfigItem label="Client Language">
          <Select
            value={currentLanguage}
            onValueChange={handleLanguageChange}
            disabled={isPatchingClient || !primaryClient?.id}
          >
            <SelectTrigger className="h-6 min-w-[100px] px-2 text-xs border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
              <SelectValue>
                <span className="truncate font-medium text-zinc-900 dark:text-white">
                  {currentLanguage}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PREFERRED_LANGUAGE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ConfigItem>
      </div>
    </div>
  );
};
